import React, { useEffect, useState } from "react";
import { Zap, Save, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/apiClient";
import { T } from "../../lib/testIds";
import { useAuth } from "../../lib/auth";

export default function Automations() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [logs, setLogs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/automations/templates").then(r => setTemplates(r.data));
    api.get("/automations/logs", { params: { limit: 100 } }).then(r => setLogs(r.data));
  };
  useEffect(() => { load(); }, []);

  const toggle = async (key, enabled) => {
    if (user.role !== "owner") return toast.error("Owner only");
    try { await api.patch(`/automations/templates/${key}`, { enabled }); toast.success(enabled ? "Automation on" : "Automation off"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const save = async () => {
    if (!editing || !draft) return;
    setSaving(true);
    try {
      await api.patch(`/automations/templates/${editing.key}`, {
        subject: draft.subject, body_sms: draft.body_sms,
        body_email_html: draft.body_email_html, body_email_text: draft.body_email_text,
        channel: draft.channel,
      });
      toast.success("Template saved");
      setEditing(null); setDraft(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-[Outfit] font-extrabold text-3xl text-[color:var(--owc-navy)]">Automations</h1>
        <p className="text-slate-600 mt-1">Toggleable SMS + email templates. Merge fields: {"{{first_name}} {{service_name}} {{scheduled_at}} {{address}} {{review_url}} {{company_phone}}"}</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-3">
          {templates.map(t => (
            <div key={t.key} data-testid={T.automations.row(t.key)} className="owc-card p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-[Outfit] font-bold text-[color:var(--owc-navy)]">{t.name}</div>
                    <span className="owc-badge-slate">{t.channel}</span>
                    <span className="text-xs text-slate-500">Trigger: {t.trigger}</span>
                  </div>
                  <p className="text-sm text-slate-600 mt-2">{t.description}</p>
                </div>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input data-testid={T.automations.toggle(t.key)} type="checkbox" className="peer sr-only" checked={t.enabled} onChange={e => toggle(t.key, e.target.checked)} disabled={user?.role !== "owner"} />
                  <span className="w-11 h-6 rounded-full bg-slate-200 peer-checked:bg-[color:var(--owc-sky)] transition-colors relative">
                    <span className="absolute top-0.5 left-0.5 h-5 w-5 bg-white rounded-full transition-transform peer-checked:translate-x-5" />
                  </span>
                </label>
              </div>
              {user?.role === "owner" && (
                <div className="mt-3">
                  <button data-testid={T.automations.edit(t.key)} onClick={() => { setEditing(t); setDraft({ ...t }); }} className="text-xs font-semibold text-[color:var(--owc-sky)]">Edit template</button>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="owc-card p-5 max-h-[75vh] overflow-y-auto" data-testid={T.automations.logs}>
          <h2 className="font-[Outfit] font-bold text-lg text-[color:var(--owc-navy)] mb-4 flex items-center gap-2"><Zap className="h-4 w-4 text-[color:var(--owc-sky)]" /> Recent activity</h2>
          {logs.length === 0 ? (
            <div className="text-slate-500 text-sm">No automation activity yet.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {logs.map(l => (
                <li key={l.id} className="py-2.5">
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-bold uppercase text-slate-500">{l.template_key.replaceAll("_", " ")}</div>
                    <span className={`owc-badge-${l.status === "sent" ? "green" : l.status === "stub" ? "slate" : l.status === "queued" ? "amber" : "red"}`}>{l.status}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1 truncate">{l.channel.toUpperCase()} → {l.to}</div>
                  {l.error && <div className="text-xs text-red-600 mt-1">{l.error}</div>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
          <div className="owc-card-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-[Outfit] font-bold text-lg">{editing.name}</h3>
              <button onClick={() => { setEditing(null); setDraft(null); }}><X className="h-4 w-4" /></button>
            </div>
            <div className="space-y-3 text-sm">
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase">Channel</label>
                <select className="owc-input mt-1" value={draft.channel} onChange={e => setDraft(d => ({ ...d, channel: e.target.value }))}>
                  <option value="sms">SMS only</option>
                  <option value="email">Email only</option>
                  <option value="both">Both</option>
                </select>
              </div>
              {(draft.channel === "email" || draft.channel === "both") && (
                <>
                  <div>
                    <label className="text-xs font-semibold text-slate-600 uppercase">Email subject</label>
                    <input className="owc-input mt-1" value={draft.subject || ""} onChange={e => setDraft(d => ({ ...d, subject: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600 uppercase">Email HTML</label>
                    <textarea className="owc-input mt-1 min-h-[110px] font-mono text-xs" value={draft.body_email_html || ""} onChange={e => setDraft(d => ({ ...d, body_email_html: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600 uppercase">Email plain text</label>
                    <textarea className="owc-input mt-1 min-h-[70px]" value={draft.body_email_text || ""} onChange={e => setDraft(d => ({ ...d, body_email_text: e.target.value }))} />
                  </div>
                </>
              )}
              {(draft.channel === "sms" || draft.channel === "both") && (
                <div>
                  <label className="text-xs font-semibold text-slate-600 uppercase">SMS body</label>
                  <textarea className="owc-input mt-1 min-h-[80px]" value={draft.body_sms || ""} onChange={e => setDraft(d => ({ ...d, body_sms: e.target.value }))} />
                  <div className="text-xs text-slate-500 mt-1">{(draft.body_sms || "").length} chars</div>
                </div>
              )}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button className="owc-btn-outline text-sm" onClick={() => { setEditing(null); setDraft(null); }}>Cancel</button>
              <button disabled={saving} onClick={save} className="owc-btn-accent text-sm inline-flex items-center gap-2">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save template</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
